import { DialogTitle } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { QUERY_KEYS } from 'constants/constants'
import trackEvents from '../../../constants/track-events'
import {
  useLanguageContext,
  useNotificationContext,
  usePartnerContext,
  usePaymentContext
} from '../../../context'
import { mutation, query } from '../../../graphql'
import { EMAIL_REGEX } from '../../../helpers'
import { trackEvent } from '../../../helpers/analytics'
import { ROLES } from '../../../helpers/authorization-roles'
import Input from '../../input'
import { useLockTexts } from '../../pricing/lock-badge'
import UpgradeInline from '../../pricing/upgrade-inline'
import Button from '../../tailwind/Button'
import Modal from '../../tailwind/Modal'
import SelectMenuWithDescription from '../../tailwind/select-menu-with-description'
import {
  PERMISSION_FEATURE,
  lockedRoleIds,
  resolveSelectedRole
} from './role-lock'
import TagsEditor, { flushTagInput } from './tags-editor'

const PERMISSION_SOURCE = 'settings_user_create'

const isUserAlreadyInGroup = (err) => {
  return (
    err.errors &&
    Array.isArray(err.errors) &&
    err.errors[0] === Object(err.errors[0]) && // is object
    err.errors[0].message === 'User is already in a Group'
  )
}

const ModalCreateUser = ({
  open,
  setOpen,
  members,
  suggestions,
  onSuccess
}) => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const { success, error } = useNotificationContext()
  const { is_trial } = usePaymentContext()
  const { partner } = usePartnerContext()

  const queryClient = useQueryClient()

  const roles = Object.values(ROLES).map(({ id, title, description }) => ({
    id,
    title: t(title),
    description: t(description)
  }))

  const [selectedRole, setSelectedRole] = useState(roles[1]) // MANAGER
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [showPermissionUpgrade, setShowPermissionUpgrade] = useState(false)

  // Ohne Berechtigungssystem (nur enforced) ist nur Admin wählbar, neue
  // Teammitglieder bekommen Admin. Der Default Manager weicht dann Admin.
  const {
    gate: permissionGate,
    label: lockedRoleLabel,
    ariaLabel: lockedRoleAriaLabel,
    includedText: lockedRoleTitle
  } = useLockTexts(PERMISSION_FEATURE, { source: PERMISSION_SOURCE })
  const lockedRoles = lockedRoleIds({ locked: permissionGate.locked })
  const role = resolveSelectedRole({
    roles,
    selected: selectedRole,
    lockedIds: lockedRoles
  })

  const emailErrorMessage = t('settings.user_management.input_email_validation')

  const formik = useFormik({
    initialValues: {
      email: '',
      firstname: '',
      lastname: ''
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .matches(EMAIL_REGEX, emailErrorMessage)
        .required(emailErrorMessage)
        .test(
          'already-invited',
          t('settings.user_management.input_email_already_invited_validation'),
          (value) =>
            !members.find(
              ({ person }) =>
                person?.email === value?.toLowerCase() ||
                person?.invitation_email === value?.toLowerCase()
            )
        ),
      firstname: Yup.string().required(
        t('settings.user_management.input_firstname_validation')
      ),
      lastname: Yup.string().required(
        t('settings.user_management.input_lastname_validation')
      )
    }),
    onSubmit: (values, { setSubmitting, resetForm }) => {
      trackEvent(trackEvents.INVITE_USER)
      setSubmitting(true)

      const { email, firstname, lastname } = values
      const finalTags = flushTagInput(tags, tagInput)

      query({
        query: 'userControl',
        variables: {
          groupname: partner.id,
          action: 'createMemberToPartner',
          email: email.toLowerCase(),
          firstname,
          lastname,
          language
        }
      })
        .then(async (response) => {
          const parsed = JSON.parse(response)
          const space_id = parsed.body.createUserSpace.res.Item.id

          await query({
            query: 'spaceControl',
            variables: {
              action: 'setAuthorizations',
              space_id,
              authorization_role: role.id
            }
          })

          if (finalTags.length > 0) {
            await mutation({
              mutation: 'updateSpace',
              input: { id: space_id, tags: finalTags }
            })
          }

          success(t('settings.user_management.invite_user_success'))

          setOpen(false)
          setSubmitting(false)

          onSuccess()

          setTimeout(() => {
            setSelectedRole(roles[1])
            setTags([])
            setTagInput('')
            setShowPermissionUpgrade(false)
            resetForm()
          }, 1000 * 0.4)

          if (is_trial) {
            queryClient.refetchQueries({
              queryKey: QUERY_KEYS.trialDataByPartner(partner.id),
              type: 'active'
            })
          }
        })
        .catch((err) => {
          setSubmitting(false)

          if (isUserAlreadyInGroup(err)) {
            error(
              t(
                'settings.user_management.already_member_in_another_company_error',
                { email: email.toLowerCase() }
              )
            )
          }
        })
    }
  })

  const handleCancel = () => {
    trackEvent(trackEvents.CANCEL_INVITE_USER)
    setOpen(false)
    setTimeout(() => {
      setSelectedRole(roles[1])
      setTags([])
      setTagInput('')
      setShowPermissionUpgrade(false)
      formik.resetForm()
    }, 1000 * 0.4)
  }

  return (
    <Modal open={open} setOpen={handleCancel} size='lg'>
      <div className='mt-4 text-center'>
        <DialogTitle
          as='h3'
          className='text-lg font-medium leading-6 text-gray-900'
        >
          {t('settings.user_management.dialog_title_create')}
        </DialogTitle>
        <p className='mx-auto mt-1 max-w-sm text-sm text-gray-500'>
          {t('settings.user_management.dialog_create_description')}
        </p>
      </div>
      <div className='mt-6'>
        <Input
          id='email'
          label={t('settings.user_management.disabled_input_email_label')}
          placeholder={t('settings.user_management.input_email_placeholder')}
          type='email'
          autoComplete='off'
          onChange={(event) =>
            formik.setFieldValue('email', event.target.value.toLowerCase())
          }
          onBlur={formik.handleBlur}
          touched={formik.touched.email}
          value={formik.values.email}
          error={formik.errors.email}
        />
        {[
          [
            'firstname',
            t('settings.user_management.input_given_name_label'),
            t('settings.user_management.input_given_name_placeholder')
          ],
          [
            'lastname',
            t('settings.user_management.input_family_name_label'),
            t('settings.user_management.input_family_name_placeholder')
          ]
        ].map((input, index) => (
          <div key={index} className='mt-4'>
            <Input
              id={input[0]}
              label={input[1]}
              placeholder={input[2]}
              type='text'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched[input[0]]}
              value={formik.values[input[0]]}
              error={formik.errors[input[0]]}
            />
          </div>
        ))}
        <div className='mt-4'>
          <SelectMenuWithDescription
            id='role'
            label={t('settings.user_management.manage_rights_label')}
            selected={role}
            setSelected={setSelectedRole}
            options={roles}
            lockedOptionIds={lockedRoles}
            onLockedSelect={() => setShowPermissionUpgrade(true)}
            lockedLabel={lockedRoleLabel}
            lockedAriaLabel={lockedRoleAriaLabel}
            lockedTitle={lockedRoleTitle}
          />
          {permissionGate.locked && (
            <>
              <p className='mt-1.5 text-xs text-gray-500'>
                {t('pricing.permission.default_admin_hint')}
              </p>
              {/* Im Modal UpgradeInline statt des Upgrade-Dialogs, HeadlessUI
                  stapelt Geschwister-Dialoge nicht sauber */}
              <div aria-live='polite'>
                {showPermissionUpgrade && (
                  <UpgradeInline
                    feature={PERMISSION_FEATURE}
                    source={PERMISSION_SOURCE}
                    compact
                    className='mt-3'
                  />
                )}
              </div>
            </>
          )}
        </div>
        <div className='mt-4'>
          <TagsEditor
            tags={tags}
            setTags={setTags}
            suggestions={suggestions}
            input={tagInput}
            setInput={setTagInput}
          />
        </div>
      </div>
      <div className='mt-12 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3'>
        <Button.SecondaryLG
          text={t('settings.user_management.dialog_cancel_action')}
          onClick={handleCancel}
          disabled={formik.isSubmitting}
        />
        <Button.PrimaryLG
          text={t('settings.user_management.dialog_submit_action')}
          onClick={formik.handleSubmit}
          isLoading={formik.isSubmitting}
        />
      </div>
    </Modal>
  )
}

ModalCreateUser.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  members: PropTypes.array,
  suggestions: PropTypes.array,
  onSuccess: PropTypes.func.isRequired
}

ModalCreateUser.defaultProps = {
  members: [],
  suggestions: []
}

export default ModalCreateUser
